import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, auth, type SiteBundle } from '../api';
import { useAsync } from '../hooks';
import { run } from '../hooks';
import type { EditorProps } from '../pages/SitePage';
import { StatusBadge } from './StatusBadge';

export function Overview({ bundle, reload, show }: EditorProps) {
  const { site, scans, versions, snippet, categories, items, questions } = bundle;
  const latest = scans[0];
  const { data: account } = useAsync(() => api.account(site.accountId).catch(() => null), [site.accountId]);
  const [daily, setDaily] = useState(site.rescan?.daily ?? false);
  const [hourUtc, setHourUtc] = useState(site.rescan?.hourUtc ?? 2);
  const saveRescan = async (next: { daily: boolean; hourUtc: number }) => {
    const r = await run(api.patchSite(site.siteId, { rescan: next }), show, next.daily ? 'Daily re-scan on' : 'Daily re-scan off');
    if (r) {
      setDaily(next.daily);
      setHourUtc(next.hourUtc);
      await reload();
    } else setDaily(site.rescan?.daily ?? false);
  };
  const localHour = (h: number) => {
    const d = new Date();
    d.setUTCHours(h, 0, 0, 0);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };
  const trial = account?.trial;
  const planLine = !account
    ? null
    : account.plan === 'paid'
      ? 'Paid plan'
      : trial?.phase === 'not_started'
        ? 'Free trial · starts at first widget load'
        : trial?.phase === 'running'
          ? `Free trial · ${trial.daysLeft} days left`
          : trial?.phase === 'grace'
            ? 'Trial ended · grace period'
            : 'Trial ended';
  return (
    <div className="grid" style={{ gap: 20 }}>
      {planLine && (
        <div className="row spread card" style={{ padding: '10px 16px' }}>
          <span className="small"><strong>{planLine}</strong>{account && account.plan === 'free' && ` · ${account.usage.scansUsed}/${account.limits.scansTotal} scans used`}</span>
          <Link className="btn sm" to={`/billing?accountId=${encodeURIComponent(site.accountId)}`}>{account?.plan === 'paid' ? 'Billing' : 'Upgrade'}</Link>
        </div>
      )}
      <div className="grid cols-4">
        <div className="stat">
          <div className="l">Categories</div>
          <div className="v">{categories.filter((c) => c.visible).length}</div>
        </div>
        <div className="stat">
          <div className="l">Cards</div>
          <div className="v">{items.filter((i) => i.visible).length}</div>
        </div>
        <div className="stat">
          <div className="l">Questions</div>
          <div className="v">{questions.filter((q) => q.enabled).length}</div>
        </div>
        <div className="stat">
          <div className="l">Config versions</div>
          <div className="v">{versions.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="row spread">
          <div>
            <h2>Daily re-scan</h2>
            <p className="muted small" style={{ margin: '4px 0 0' }}>
              Once a day the site is crawled again; only pages whose content changed are re-analysed, and a draft is prepared when something changed. Paid plan.
            </p>
          </div>
          <div className="row">
            <label className="field" style={{ width: 150 }}>
              <span>Run at (your time)</span>
              <select className="select" value={hourUtc} disabled={!daily} onChange={(e) => void saveRescan({ daily, hourUtc: Number(e.target.value) })}>
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{localHour(h)}</option>
                ))}
              </select>
            </label>
            <label className="checkbox" title={account && account.plan !== 'paid' ? 'Upgrade to the paid plan to enable daily re-scans' : undefined}>
              <input type="checkbox" checked={daily} disabled={!!account && account.plan !== 'paid' && !daily} onChange={(e) => void saveRescan({ daily: e.target.checked, hourUtc })} /> enabled
            </label>
          </div>
        </div>
        {site.rescan?.lastAutoScanAt && (
          <p className="small muted" style={{ marginTop: 8 }}>Last automatic scan {new Date(site.rescan.lastAutoScanAt).toLocaleString()}{site.rescan.lastAutoScanId ? ` (${site.rescan.lastAutoScanId})` : ''}.</p>
        )}
      </div>

      <PreviewLinks site={site} show={show} />

      <div className="grid cols-2">
        <div className="card">
          <div className="row spread">
            <h2>Install</h2>
            <button className="btn sm" onClick={() => navigator.clipboard.writeText(snippet).then(() => show('Snippet copied'))}>
              Copy
            </button>
          </div>
          <InstallStatus install={site.install} onCheck={() => void reload()} />
          <p className="muted small" style={{ margin: '8px 0 10px' }}>
            Paste before <span className="mono">&lt;/body&gt;</span> on every page, or add as a Custom HTML tag in your tag manager.
          </p>
          <pre className="snippet">{snippet}</pre>
          <p className="muted small" style={{ marginTop: 10 }}>
            Allowed origins: {site.allowedOrigins.join(', ')}
          </p>
        </div>
        <div className="card">
          <div className="row spread">
            <h2>Latest scan</h2>
            <Link className="btn sm" to={`/sites/${site.siteId}/pages`}>
              Pages &amp; re-scan
            </Link>
          </div>
          {latest ? (
            <div style={{ marginTop: 10 }}>
              <div className="row">
                <StatusBadge status={latest.status} />
                <span className="small muted">
                  {latest.mode === 'category' ? `category ${latest.categoryId}` : latest.trigger === 'scheduled' ? 'daily re-scan' : 'full scan'}
                  {auth.token && ` · ${latest.analyser}`}
                </span>
              </div>
              <p style={{ marginTop: 8 }}>{latest.status === 'failed' ? latest.error : latest.message}</p>
              <p className="small muted">
                {latest.pagesCrawled ?? 0} pages crawled · {latest.pagesClassified ?? 0} analysed{auth.token && ` · cost US$${(latest.costUsd ?? 0).toFixed(2)}`} · started{' '}
                {new Date(latest.startedAt).toLocaleString()}
              </p>
            </div>
          ) : (
            <p className="muted">No scans yet.</p>
          )}
          {site.siteSummary && <p className="small muted" style={{ marginTop: 12 }}>{site.siteSummary}</p>}
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Version</th>
              <th>Status</th>
              <th>Change</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.version}>
                <td>v{v.version}</td>
                <td>
                  <span className={`badge ${v.status === 'published' ? 'accent' : v.status === 'draft' ? 'warn' : ''}`}>{v.status}</span>
                </td>
                <td>{v.changeSummary}</td>
                <td className="small muted">{new Date(v.createdAt).toLocaleString()}</td>
                <td style={{ textAlign: 'right' }}>
                  {v.status !== 'published' && (
                    <button
                      className="btn sm"
                      onClick={async () => {
                        const r = await run(api.publishVersion(site.siteId, v.version), show, `v${v.version} published`);
                        if (r) await reload();
                      }}
                    >
                      {v.status === 'draft' ? 'Publish this draft' : 'Roll back to this'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {versions.length === 0 && (
              <tr>
                <td colSpan={5} className="muted">
                  No versions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/** FR-C-10: whether the snippet has been seen loading from the customer's site. */
function InstallStatus({ install, onCheck }: { install?: { lastSeenAt: string; origin?: string }; onCheck: () => void }) {
  const ago = (iso: string) => {
    const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
    return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
  };
  const recent = install && Date.now() - Date.parse(install.lastSeenAt) < 7 * 86400e3;
  return (
    <div className="row spread" style={{ margin: '10px 0 4px' }}>
      {install ? (
        <span className={`badge ${recent ? 'ok' : 'warn'}`} title={new Date(install.lastSeenAt).toLocaleString()}>
          {recent ? 'Installed' : 'Not seen recently'} · last load {ago(install.lastSeenAt)}{install.origin ? ` on ${install.origin}` : ''}
        </span>
      ) : (
        <span className="badge warn">Not detected yet · no widget load seen from your site</span>
      )}
      <button className="btn sm" onClick={onCheck}>Check again</button>
    </div>
  );
}


/** FR-C-05: preview links for the work in progress and the live version, on a neutral stand-in page. */
function PreviewLinks({ site, show }: { site: SiteBundle['site']; show: (t: string, err?: boolean) => void }) {
  const wip = api.demoUrl({ config: api.previewConfigUrl(site.siteId), preview: '' });
  const live = site.publishedConfigVersion ? api.demoUrl({ key: site.siteKey, preview: '' }) : null;
  const row = (label: string, hint: string, url: string | null) => (
    <div className="row" style={{ alignItems: 'flex-end' }}>
      <label className="field" style={{ flex: 1, minWidth: 260 }}>
        <span>
          {label} <span className="muted">· {hint}</span>
        </span>
        <input className="input mono" readOnly value={url ?? 'Nothing published yet'} onFocus={(e) => e.currentTarget.select()} />
      </label>
      <button className="btn sm" disabled={!url} onClick={() => url && navigator.clipboard.writeText(url).then(() => show('Link copied'))}>
        Copy
      </button>
      {url ? (
        <a className="btn sm" href={url} target="_blank" rel="noopener">
          Open in new tab
        </a>
      ) : (
        <span className="btn sm" aria-disabled="true" style={{ opacity: 0.5 }}>
          Open in new tab
        </span>
      )}
    </div>
  );
  return (
    <div className="card grid" style={{ gap: 12 }}>
      <div>
        <h2>Preview</h2>
        <p className="muted small" style={{ margin: '4px 0 0' }}>
          The Springboard on a neutral stand-in page. Previews open immediately and send no events. The work-in-progress link carries your sign-in, so keep it to yourself; the live link
          is safe to share.
        </p>
      </div>
      {row('Work in progress', 'your unpublished changes', wip)}
      {row(`Live${site.publishedConfigVersion ? ` (v${site.publishedConfigVersion})` : ''}`, 'what visitors in the Springboard arm see today', live)}
    </div>
  );
}
